/**
 * Dashboard — the tenant's lead list with enrichment status, batch summary,
 * status filter, and free-text search. Each row links to the full Scientific
 * Lead Intelligence Profile (where the five scores + recommendation live).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { EnrichmentJobStatus, Lead, LeadBatch } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'complete', label: 'Complete' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'failed', label: 'Failed' },
];

function StatusPill({ status }: { status: EnrichmentJobStatus }): JSX.Element {
  return (
    <span className={`pill pill-${status}`}>{status.replace('_', ' ')}</span>
  );
}

export function Dashboard(): JSX.Element {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [batches, setBatches] = useState<LeadBatch[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useMemo(
    () =>
      async (statusFilter: string, q: string): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
          const params: Record<string, string | number> = { limit: 100 };
          if (statusFilter) params.status = statusFilter;
          if (q.trim()) params.q = q.trim();
          const [leadPage, batchPage] = await Promise.all([
            api.listLeads(params),
            api.listBatches(),
          ]);
          setLeads(leadPage.items);
          setTotal(leadPage.total);
          setBatches(batchPage.items);
        } catch (e) {
          setError(
            e instanceof ApiClientError ? e.message : 'Failed to load leads',
          );
        } finally {
          setLoading(false);
        }
      },
    [],
  );

  // Reload whenever the status filter changes; free-text search is applied on
  // submit (so it does not refetch on every keystroke). `load` is stable.
  useEffect(() => {
    void load(status, search);
  }, [status, load]);

  const onSearch = (e: FormEvent): void => {
    e.preventDefault();
    void load(status, search);
  };

  return (
    <div className="dashboard">
      <div className="page-head">
        <h1>Leads</h1>
        <Link to="/upload" className="btn btn-primary">
          Upload CSV
        </Link>
      </div>

      {batches.length > 0 && (
        <div className="batch-strip">
          {batches.slice(0, 6).map((b) => (
            <div key={b.id} className="batch-chip">
              <span className="batch-name">{b.filename}</span>
              <span className="muted">
                {b.processedLeads}/{b.totalLeads} · {b.status}
              </span>
            </div>
          ))}
        </div>
      )}

      <form className="filters" onSubmit={onSearch}>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search institution or title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="btn">
          Search
        </button>
        <span className="muted result-count">{total} total</span>
      </form>

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="muted centered">Loading leads…</div>
      ) : leads.length === 0 ? (
        <div className="empty-state muted">
          No leads yet. Upload a CSV to get started.
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Institution</th>
              <th>Title</th>
              <th>Source</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td className="lead-name">{lead.fullName}</td>
                <td>{lead.institutionRaw ?? '—'}</td>
                <td>{lead.title ?? '—'}</td>
                <td>{lead.source.replace('_', ' ')}</td>
                <td>
                  <StatusPill status={lead.enrichmentStatus} />
                </td>
                <td>
                  <Link to={`/leads/${lead.id}`} className="btn btn-small">
                    View profile
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
